/**
 * SlicerBar — the multi-dimension filter row above setup analytics.
 *
 * Renders one chip per dimension (Time, VIX, Direction, Day, Range) plus a
 * summary line showing active filter count + visible/total trades + a
 * Clear-all action + a clipboard-copy summary.
 *
 * Wraps `useSliceState` indirectly — the parent owns the slice state and
 * passes it in as `slice` + action callbacks. This keeps SlicerBar pure
 * and easy to reuse on other analytics pages (Confluences tab, ATM tab,
 * etc.) without rewiring storage.
 *
 * Live counts:
 *   For each dropdown, per-value counts are computed from the trades that
 *   would match if THIS dimension were the only active one (i.e., the OTHER
 *   active dims are still applied as scope). That way the counts reflect
 *   "if I add this value, how many trades survive?" — the question the
 *   user actually has when picking.
 */

import { useMemo } from 'react';
import { Filter, Clipboard, Eraser } from 'lucide-react';
import SlicerChipDropdown from './SlicerChipDropdown';
import GlassSelect from '../Common/GlassSelect';
import {
  TIME_BUCKETS,
  DAY_BUCKETS,
  DIRECTION_BUCKETS,
  RANGE_PRESETS,
  filterTradesBySlice,
  enumerateValuesInScope,
  summarizeSlice,
  hasAnyActiveDimension,
} from '../../utils/sliceFilters';

const SlicerBar = ({
  // ── data ──
  baseTrades = [],          // trades the page would analyze if no slice was active (already globally-filtered)
  confluenceLookup = {},    // confluenceId → { name, category } map
  // ── slice state (from useSliceState in the parent) ──
  slice,
  setDimension,             // (dim, valuesArray) => void
  setRange,                 // (presetId) => void
  clearDimension,           // (dim) => void
  clearAll,                 // () => void
  // ── optional callbacks ──
  onCopySummary,            // (summaryString) => void — parent can show toast
  // ── presentation ──
  darkMode = true,
  // ── "Pattern Fired" + similar dims that depend on bar-analysis backfill ──
  needsBackfillCount = 0,   // count of trades lacking latest bar-analysis fields
}) => {
  // Sliced trades after applying current slice (for the live "X of Y" summary)
  const slicedTrades = useMemo(
    () => filterTradesBySlice(baseTrades, slice, { confluenceLookup }),
    [baseTrades, slice, confluenceLookup]
  );

  // Per-dimension dropdown options with live counts. Each dim's counts are
  // computed against the trades that would pass if THIS dim were temporarily
  // ignored — answering "if I add this value, how many survive?"
  const optionsFor = useMemo(() => {
    const buildScopeIgnoringDim = (dim) => {
      const tempSlice = { ...slice, [dim]: dim === 'range' ? 'all' : [] };
      return filterTradesBySlice(baseTrades, tempSlice, { confluenceLookup });
    };

    const buildTimeOpts = () => {
      const scope = buildScopeIgnoringDim('time');
      const counts = enumerateValuesInScope(scope, 'time', { confluenceLookup });
      return TIME_BUCKETS.map(b => ({ id: b.id, label: b.label, count: counts[b.id] || 0 }));
    };
    const buildDirOpts = () => {
      const scope = buildScopeIgnoringDim('direction');
      const counts = enumerateValuesInScope(scope, 'direction', { confluenceLookup });
      return DIRECTION_BUCKETS.map(b => ({ id: b.id, label: b.label, count: counts[b.id] || 0 }));
    };
    const buildDayOpts = () => {
      const scope = buildScopeIgnoringDim('dayOfWeek');
      const counts = enumerateValuesInScope(scope, 'dayOfWeek', { confluenceLookup });
      return DAY_BUCKETS.map(b => ({ id: b.id, label: b.label, count: counts[b.id] || 0 }));
    };
    const buildVixOpts = () => {
      const scope = buildScopeIgnoringDim('vix');
      const counts = enumerateValuesInScope(scope, 'vix', { confluenceLookup });
      // Discover all VIX chip names from the confluence lookup (so users see
      // every VIX bucket they've defined, even ones with 0 matches in scope).
      const allVixNames = new Set();
      for (const conf of Object.values(confluenceLookup || {})) {
        const cat = (conf?.category || '').toUpperCase();
        const name = conf?.name || '';
        if (cat === 'VIX' || name.toUpperCase().startsWith('VIX ')) {
          allVixNames.add(name);
        }
      }
      // Merge discovered names (in case a trade has a VIX chip not in the lookup)
      Object.keys(counts).forEach(k => allVixNames.add(k));
      return [...allVixNames]
        .sort()
        .map(name => ({ id: name, label: name, count: counts[name] || 0 }));
    };

    return {
      time: buildTimeOpts(),
      direction: buildDirOpts(),
      dayOfWeek: buildDayOpts(),
      vix: buildVixOpts(),
    };
  }, [baseTrades, slice, confluenceLookup]);

  const isActive = hasAnyActiveDimension(slice);
  const baseCount = baseTrades.length;
  const slicedCount = slicedTrades.length;

  const handleCopy = () => {
    const summary = `${summarizeSlice(slice)} · ${slicedCount} of ${baseCount} trades`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(summary).then(
        () => onCopySummary?.(summary),
        () => onCopySummary?.(summary)  // fallback — still notify parent
      );
    } else {
      onCopySummary?.(summary);
    }
  };

  const activeDimCount = (() => {
    let n = 0;
    if (slice?.time?.length      > 0) n++;
    if (slice?.vix?.length       > 0) n++;
    if (slice?.direction?.length > 0) n++;
    if (slice?.dayOfWeek?.length > 0) n++;
    if (slice?.range && slice.range !== 'all') n++;
    return n;
  })();

  return (
    <div className={`rounded-lg border p-3 ${
      darkMode ? 'bg-gray-900/40 border-gray-700/50' : 'bg-white/40 border-gray-200'
    }`}>
      {/* Header label */}
      <div className="flex items-center gap-2 mb-2">
        <Filter size={12} className={darkMode ? 'text-purple-400' : 'text-purple-600'} />
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${
          darkMode ? 'text-purple-300' : 'text-purple-700'
        }`}>
          Slice this setup
        </span>
      </div>

      {/* Chip row */}
      <div className="flex flex-wrap items-center gap-1.5">
        <SlicerChipDropdown
          label="Time"
          values={slice?.time || []}
          options={optionsFor.time}
          totalKnownValues={TIME_BUCKETS.length}
          onApply={(v) => setDimension('time', v)}
          onClear={() => clearDimension('time')}
          darkMode={darkMode}
        />
        <SlicerChipDropdown
          label="VIX"
          values={slice?.vix || []}
          options={optionsFor.vix}
          totalKnownValues={optionsFor.vix.length}
          onApply={(v) => setDimension('vix', v)}
          onClear={() => clearDimension('vix')}
          darkMode={darkMode}
        />
        <SlicerChipDropdown
          label="Direction"
          values={slice?.direction || []}
          options={optionsFor.direction}
          totalKnownValues={DIRECTION_BUCKETS.length}
          onApply={(v) => setDimension('direction', v)}
          onClear={() => clearDimension('direction')}
          darkMode={darkMode}
        />
        <SlicerChipDropdown
          label="Day"
          values={slice?.dayOfWeek || []}
          options={optionsFor.dayOfWeek}
          totalKnownValues={DAY_BUCKETS.length}
          onApply={(v) => setDimension('dayOfWeek', v)}
          onClear={() => clearDimension('dayOfWeek')}
          darkMode={darkMode}
        />
        {/* Range — single-select, modeled as a dropdown that submits one ID */}
        <RangeChip
          value={slice?.range || 'all'}
          onChange={(presetId) => setRange(presetId)}
          darkMode={darkMode}
        />
      </div>

      {/* Summary footer */}
      <div className={`flex items-center gap-3 mt-2 pt-2 border-t text-[10px] ${
        darkMode ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'
      }`}>
        <span className="tabular-nums">
          <span className={isActive ? (darkMode ? 'text-purple-200 font-semibold' : 'text-purple-700 font-semibold') : ''}>
            {slicedCount}
          </span>
          <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}> of {baseCount} trades</span>
        </span>
        {activeDimCount > 0 && (
          <span className={darkMode ? 'text-purple-300' : 'text-purple-700'}>
            · {activeDimCount} active filter{activeDimCount === 1 ? '' : 's'}
          </span>
        )}
        {needsBackfillCount > 0 && (
          <span className={darkMode ? 'text-amber-300' : 'text-amber-700'} title="Some trades lack bar-analysis data needed by Pattern / Discipline / Trend filters">
            · ⚠ {needsBackfillCount} need backfill
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleCopy}
          title="Copy slice summary to clipboard"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded transition-colors ${
            darkMode ? 'hover:text-gray-200 hover:bg-white/5' : 'hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <Clipboard size={10} />
          <span>Copy</span>
        </button>
        {isActive && (
          <button
            type="button"
            onClick={clearAll}
            title="Clear all filters"
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded transition-colors ${
              darkMode ? 'text-amber-300 hover:bg-amber-500/10' : 'text-amber-700 hover:bg-amber-50'
            }`}
          >
            <Eraser size={10} />
            <span>Clear all</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ── Range chip — single-select preset dropdown ──
//
// The one outlier from the v7.5.15 GlassSelect rollout. The other 36
// native <select>s converted to plain GlassSelect, but this one needed
// the `renderTrigger` extension (added 2026-05-27) because:
//   1. Pill shape with conditional purple-when-active fill — the default
//      GlassSelect trigger styling is rectangular like the other inputs.
//   2. "Range: X" trigger label when active vs plain "X" in the options —
//      different text in the trigger than in the option list.
// Both behaviors are preserved by using renderTrigger to fully control
// the trigger markup, while the popup gets GlassSelect's frosted-glass
// dropdown for free.
const RangeChip = ({ value, onChange, darkMode }) => {
  const effectiveValue = value || 'all';
  const isOff = !value || value === 'all';

  return (
    <GlassSelect
      value={effectiveValue}
      onChange={(v) => onChange(v)}
      options={RANGE_PRESETS.map(p => ({ value: p.id, label: p.label }))}
      darkMode={darkMode}
      ariaLabel="Date range filter"
      wrapperClassName="relative inline-block"
      renderTrigger={(triggerProps, { selectedOption }) => {
        const pillClass = isOff
          ? (darkMode
              ? 'bg-transparent border-gray-700 text-gray-300 hover:border-gray-500'
              : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400')
          : (darkMode
              ? 'bg-purple-500/20 border-purple-400/50 text-purple-100 ring-1 ring-purple-400/30'
              : 'bg-purple-100 border-purple-400 text-purple-800 ring-1 ring-purple-300/50');
        const labelText = isOff
          ? (selectedOption?.label || 'All')
          : `Range: ${selectedOption?.label || ''}`;
        return (
          <button
            type="button"
            {...triggerProps}
            className={`relative pl-2.5 pr-6 py-1 text-[11px] rounded-full border transition-colors cursor-pointer ${pillClass}`}
          >
            {labelText}
            {/* Decorative chevron — matches the visual of the original
                native-select version so the chip doesn't shift size. */}
            <span className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[8px] ${
              darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              ▾
            </span>
          </button>
        );
      }}
    />
  );
};

export default SlicerBar;
